import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';

// Auth Modal Component
const AuthModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const endpoint = isSignup ? `${API_URL}/api/auth/signup` : `${API_URL}/api/auth/login`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        onLoginSuccess(data.token);
      } else {
        setError(data.error || 'Authentication failed');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#f5f1ed',
        borderRadius: 8,
        padding: '2rem',
        maxWidth: 400,
        width: '90%',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
      }}>
        <h2 style={{
          fontFamily: 'var(--font-display, "EB Garamond", serif)',
          color: '#FFFFFF',
          marginBottom: '0.5rem',
          fontSize: '1.8rem'
        }}>
          Bearing Intelligence
        </h2>
        <p style={{ color: '#667585', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
          {isSignup ? 'Create an account' : 'Sign in to continue'}
        </p>

        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              marginBottom: '0.75rem',
              border: '1px solid #667585',
              borderRadius: 4,
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              boxSizing: 'border-box'
            }}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              marginBottom: '1rem',
              border: '1px solid #667585',
              borderRadius: 4,
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              boxSizing: 'border-box'
            }}
          />

          <button
            type="submit"
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: '#E74C3C',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 4,
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.95rem',
              marginBottom: '0.75rem'
            }}
          >
            {isSignup ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        {error && <p style={{ color: '#d32f2f', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{error}</p>}

        <p style={{ textAlign: 'center', color: '#667585', fontSize: '0.85rem' }}>
          {isSignup ? 'Have an account?' : "Don't have an account?"}{' '}
          <button
            type="button"
            onClick={() => {
              setIsSignup(!isSignup);
              setError('');
              setEmail('');
              setPassword('');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#E74C3C',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontWeight: 600
            }}
          >
            {isSignup ? 'Sign in' : 'Sign up'}
          </button>
        </p>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '0.5rem',
            marginTop: '1rem',
            backgroundColor: 'transparent',
            color: '#667585',
            border: '1px solid #667585',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: '0.85rem'
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

// Main Geographic Landing Page
export const GeographicLandingPage = ({ onLoginSuccess }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load the geographic assessment app
    const loadGeographicAssessment = async () => {
      try {
        // Note: In production, the geographic assessment would be served from this app
        // For now, we're creating a placeholder that will be populated with the actual assessment content
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to load geographic assessment:', err);
        setIsLoading(false);
      }
    };

    loadGeographicAssessment();
  }, []);

  return (
    <div style={{ width: '100%', minHeight: '100vh', position: 'relative', overflow: 'hidden', backgroundColor: '#F5F1E8' }}>
      {/* Background placeholder — geographic assessment unavailable in production */}
      {isLoading && (
        <div style={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          color: '#888'
        }}>
          <p>Loading...</p>
        </div>
      )}

      {/* Sign-In Overlay Button */}
      <button
        onClick={() => setShowAuthModal(true)}
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          padding: '0.875rem 1.5rem',
          backgroundColor: '#E74C3C',
          color: '#FFFFFF',
          border: 'none',
          borderRadius: 4,
          fontWeight: 600,
          fontSize: '0.95rem',
          cursor: 'pointer',
          zIndex: 100,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#b39550'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#E74C3C'}
      >
        Enter Full Assessment
      </button>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={(token) => {
          setShowAuthModal(false);
          onLoginSuccess(token);
        }}
      />

      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#f5f1ed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <p style={{ color: '#667585' }}>Loading Geographic Assessment...</p>
        </div>
      )}
    </div>
  );
};
